import { Box, Paper, Typography, IconButton, styled, Chip, Tooltip } from '@mui/material';
import { 
  Delete as DeleteIcon, 
  Edit as EditIcon, 
  SwapHoriz as SubstituteIcon, 
  Room as RoomIcon, 
  Warning as WarningIcon,
  Security as SupervisionIcon,
  PersonOff as AbsentIcon,
} from '@mui/icons-material';
import type { TimetableRecordWithRelations, DraftAssignment } from '@timetable/types';
import type { Conflict } from './ConflictVisualizationPanel';

const CardContainer = styled(Paper, {
  shouldForwardProp: (prop) => 
    prop !== 'subjectColor' && 
    prop !== 'isSelected' && 
    prop !== 'hasConflict' && 
    prop !== 'conflictSeverity' &&
    prop !== 'isSupervision',
})<{ 
  subjectColor?: string; 
  isSelected?: boolean; 
  hasConflict?: boolean; 
  conflictSeverity?: 'hard' | 'soft';
  isSupervision?: boolean;
}>(({ theme, subjectColor, isSelected, hasConflict, conflictSeverity, isSupervision }) => ({
  padding: theme.spacing(0.5, 1),
  cursor: 'default',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  borderLeft: `4px solid ${subjectColor || theme.palette.primary.main}`,
  borderRadius: '6px',
  opacity: 1,
  backgroundColor: isSelected ? theme.palette.action.selected : theme.palette.background.paper,
  minHeight: '52px',
  width: '100%',
  // Supervision styling (Requirements 22.13, 22.14, 22.15)
  ...(isSupervision && {
    backgroundColor: '#f0fdf4', // Light green background for supervision
    borderLeft: `4px solid ${theme.palette.success.main}`,
  }),
  // Conflict highlighting (Requirements 11.4, 11.5, 8.7)
  ...(hasConflict && {
    border: `2px solid ${conflictSeverity === 'hard' ? theme.palette.error.main : theme.palette.warning.main}`,
    backgroundColor: conflictSeverity === 'hard' ? theme.palette.error.light + '10' : theme.palette.warning.light + '10',
  }),
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

const CardHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 4,
});

const CardActions = styled(Box)({
  display: 'flex',
  gap: 4,
  opacity: 0,
  transition: 'opacity 0.2s',
  '*:hover > &, .MuiPaper-root:hover &': {
    opacity: 1,
  },
});

interface PeriodCardProps {
  record: TimetableRecordWithRelations;
  viewMode: 'class' | 'teacher' | 'subject';
  isSelected?: boolean;
  conflicts?: Conflict[]; // Conflicts affecting this record (Requirements 11.4, 11.5, 11.9)
  onEdit?: (record: TimetableRecordWithRelations) => void;
  onDelete?: (record: TimetableRecordWithRelations) => void;
  // Substitution overlay props (Requirements 17.2, 17.3, 17.6, 17.7, 17.8)
  substitution?: DraftAssignment | null;
  isAbsent?: boolean;
  isCovering?: boolean;
  isOverlayMode?: boolean; // When true, card is read-only with overlay styling
}

export function PeriodCard({ 
  record, 
  viewMode, 
  isSelected, 
  conflicts = [], 
  onEdit, 
  onDelete,
  substitution,
  isAbsent,
  isCovering,
  isOverlayMode = false,
}: PeriodCardProps) {
  // Check if this is a supervision assignment (Requirement 22.13)
  const isSupervision = record.is_supervision || false;

  // Substitution overlay logic (Requirements 17.2, 17.3, 17.6, 17.7, 17.8)
  const hasSubstitutionOverlay = isOverlayMode && (substitution || isAbsent || isCovering);
  
  // Determine overlay display text
  const getOverlayText = () => {
    if (isAbsent && substitution?.substitute_teacher) {
      // Requirement 17.7: "Absent — Covered by [name]"
      return `Absent — Covered by ${substitution.substitute_teacher.name}`;
    }
    if (isAbsent && !substitution?.substitute_teacher) {
      // Requirement 17.7: "Absent — Unassigned"
      return 'Absent — Unassigned';
    }
    if (isCovering && substitution?.absent_teacher) {
      // Requirement 17.6: Show covering badge with absent teacher name
      return `Covering for ${substitution.absent_teacher.name}`;
    }
    return null;
  };

  const overlayText = getOverlayText();

  // Requirements 8.1, 8.2: Subject name prominent, teacher name below in smaller font
  const getPrimaryText = () => {
    // Overlay mode: show substitute teacher name for class view (Requirement 17.2)
    if (isOverlayMode && substitution?.substitute_teacher && viewMode === 'class') {
      return substitution.substitute_teacher.name;
    }
    
    // Requirement 22.14: Show supervision type for supervision assignments
    if (isSupervision) {
      // Get supervision type from period if available
      const supervisionType = record.period?.supervision_type || 'Supervision';
      return supervisionType.charAt(0).toUpperCase() + supervisionType.slice(1) + ' Duty';
    }
    
    switch (viewMode) {
      case 'class':
        return record.subject?.subject_name || 'Unknown Subject'; // Subject prominent for class view
      case 'teacher':
        return `${record.class_section?.class?.class_name || 'Unknown'}-${record.class_section?.section || '?'}`;
      case 'subject':
        return record.teacher?.name || 'Unknown Teacher';
      default:
        return '';
    }
  };

  const getSecondaryText = () => {
    // Overlay mode: show original teacher name in tooltip (Requirement 17.3)
    if (isOverlayMode && substitution?.absent_teacher && viewMode === 'class') {
      return `(Original: ${substitution.absent_teacher.name})`;
    }
    
    // Requirement 22.15: List assigned teachers for supervision
    if (isSupervision) {
      return record.teacher?.name || 'Unassigned';
    }
    
    switch (viewMode) {
      case 'class':
        return record.teacher?.name || 'Unknown Teacher'; // Teacher below subject for class view
      case 'teacher':
        return record.subject?.subject_name || 'Unknown Subject';
      case 'subject':
        return `${record.class_section?.class?.class_name || 'Unknown'}-${record.class_section?.section || '?'}`;
      default:
        return '';
    }
  };

  const getTertiaryText = () => {
    // Requirement 22.14: Show supervision location for supervision assignments
    if (isSupervision && record.supervision_location) {
      return `📍 ${record.supervision_location}`;
    }
    // No tertiary text needed for class view (subject + teacher is sufficient)
    return null;
  };

  // Conflict detection (Requirements 11.4, 11.5, 11.9)
  const hasConflict = conflicts.length > 0;
  const conflictSeverity = conflicts.some(c => c.severity === 'hard') ? 'hard' : 'soft';
  
  // Generate conflict tooltip text (Requirement 11.5)
  const getConflictTooltip = () => {
    if (!hasConflict) return '';
    
    const conflictMessages = conflicts.map(c => {
      // Color coding by type (Requirement 11.9)
      const typeLabel = c.type === 'teacher' ? '👤 Teacher' : c.type === 'room' ? '🏠 Room' : '📚 Class';
      return `${typeLabel}: ${c.message}`;
    });
    
    return conflictMessages.join('\n');
  };

  // Generate aria-label for accessibility
  const getAriaLabel = () => {
    if (hasSubstitutionOverlay && overlayText) {
      return `${overlayText}. ${isOverlayMode ? 'Read-only overlay. Edit substitutions from the Absences page.' : ''}`;
    }
    if (isSupervision) {
      return `Supervision duty: ${getPrimaryText()} by ${getSecondaryText()}${record.supervision_location ? ` at ${record.supervision_location}` : ''}${hasConflict ? '. Has conflicts.' : ''}. Press Enter or Space to select, use arrow keys to navigate.`;
    }
    return `${record.teacher?.name || 'Unknown'} teaching ${record.subject?.subject_name || 'Unknown'}${record.is_substitution ? ' (Substitute)' : ''}${hasConflict ? '. Has conflicts.' : ''}. Press Enter or Space to select, use arrow keys to navigate.`;
  };

  // Requirement 17.9: Overlay cells are read-only with tooltip
  const overlayTooltip = isOverlayMode 
    ? 'Edit substitutions from the Absences page.' 
    : (hasConflict ? getConflictTooltip() : '');

  return (
    <Tooltip 
      title={overlayTooltip || (hasConflict ? getConflictTooltip() : '')} 
      arrow 
      placement="top"
      componentsProps={{
        tooltip: {
          sx: { whiteSpace: 'pre-line', maxWidth: 300 }
        }
      }}
    >
      <CardContainer
        elevation={2}
        subjectColor={!isSupervision ? (record.subject?.color_code || undefined) : undefined}
        isSelected={isSelected}
        hasConflict={hasConflict}
        conflictSeverity={conflictSeverity}
        isSupervision={isSupervision}
        data-testid="period-card"
        data-record-id={record.id}
        role="button"
        aria-label={getAriaLabel()}
        sx={{
          // Overlay mode styling (Requirements 17.2, 17.6, 17.7)
          ...(hasSubstitutionOverlay && {
            backgroundColor: isAbsent ? '#fee' : isCovering ? '#ffe' : undefined,
            borderLeft: isAbsent ? '4px solid #f44336' : isCovering ? '4px solid #ff9800' : undefined,
            cursor: 'default',
            '&:hover': {
              boxShadow: 2,
              transform: 'none',
            },
          }),
        }}
      >
        <CardHeader>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Substitution overlay badges (Requirements 17.2, 17.6, 17.7) */}
            {hasSubstitutionOverlay && (
              <Chip
                icon={isAbsent ? <AbsentIcon /> : <SubstituteIcon />}
                label={isAbsent ? 'Absent' : isCovering ? 'Covering' : 'Substitute'}
                size="small"
                color={isAbsent ? 'error' : 'warning'}
                sx={{ 
                  mb: 0.5, 
                  height: 20,
                  fontSize: '0.7rem',
                  '& .MuiChip-icon': {
                    fontSize: '0.9rem',
                    marginLeft: '4px',
                  }
                }}
              />
            )}
            {/* Requirement 22.13: Supervision duty icon */}
            {isSupervision && (
              <Chip
                icon={<SupervisionIcon />}
                label="Supervision"
                size="small"
                color="success"
                sx={{ 
                  mb: 0.5, 
                  height: 20,
                  fontSize: '0.7rem',
                  '& .MuiChip-icon': {
                    fontSize: '0.9rem',
                    marginLeft: '4px',
                  }
                }}
              />
            )}
            {/* Conflict warning indicator (Requirements 11.4, 11.5) */}
            {hasConflict && (
              <Chip
                icon={<WarningIcon />}
                label={conflictSeverity === 'hard' ? 'Conflict' : 'Warning'}
                size="small"
                color={conflictSeverity === 'hard' ? 'error' : 'warning'}
                sx={{ 
                  mb: 0.5, 
                  height: 20,
                  fontSize: '0.7rem',
                  '& .MuiChip-icon': {
                    fontSize: '0.9rem',
                    marginLeft: '4px',
                  }
                }}
              />
            )}
            {/* Requirement 8.6: Substitution indicator chip */}
            {!isSupervision && record.is_substitution && (
              <Chip
                icon={<SubstituteIcon />}
                label="Substitute"
                size="small"
                color="warning"
                sx={{ 
                  mb: 0.5, 
                  height: 20,
                  fontSize: '0.7rem',
                  '& .MuiChip-icon': {
                    fontSize: '0.9rem',
                    marginLeft: '4px',
                  }
                }}
              />
            )}
          {/* Show overlay text for absent/covering periods */}
          {overlayText && (
            <Typography
              variant="body2"
              fontWeight={600}
              fontSize="12px"
              color={isAbsent ? 'error.main' : 'warning.main'}
              sx={{ lineHeight: 1.2, mb: 0.5 }}
            >
              {overlayText}
            </Typography>
          )}
          {/* Requirement 8.1: Subject name prominent (bold, larger font - 14px) */}
          <Typography
            variant="body2"
            fontWeight={600}
            fontSize="13px"
            noWrap
            title={getPrimaryText()}
            sx={{ lineHeight: 1.2 }}
          >
            {getPrimaryText()}
          </Typography>
          {/* Requirement 8.2: Teacher name below in smaller, secondary font (12px) */}
          <Typography
            variant="caption"
            fontSize="11px"
            color="text.secondary"
            noWrap
            title={getSecondaryText()}
            sx={{ lineHeight: 1.2, mt: 0.25 }}
          >
            {getSecondaryText()}
          </Typography>
          {getTertiaryText() && (
            <Typography
              variant="caption"
              fontSize="12px"
              color="text.secondary"
              display="block"
              noWrap
              title={getTertiaryText() || ''}
              sx={{ lineHeight: 1.3, mt: 0.25 }}
            >
              {getTertiaryText()}
            </Typography>
          )}
          {/* Requirement 8.3: Optional room display */}
          {!isSupervision && record.room && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <RoomIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled" noWrap>
                {record.room.name}
              </Typography>
            </Box>
          )}
          {/* Show supervision notes if available */}
          {isSupervision && record.supervision_notes && (
            <Typography
              variant="caption"
              fontSize="11px"
              color="text.disabled"
              sx={{ 
                mt: 0.5, 
                fontStyle: 'italic',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              title={record.supervision_notes}
            >
              {record.supervision_notes}
            </Typography>
          )}
        </Box>

        <CardActions>
          {onEdit && !isOverlayMode && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(record);
              }}
              sx={{ padding: 0.5 }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {onDelete && !isOverlayMode && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(record);
              }}
              sx={{ padding: 0.5 }}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </CardActions>
      </CardHeader>
    </CardContainer>
    </Tooltip>
  );
}
